import React, { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Badge,
  useTheme,
  useMediaQuery,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography as MuiTypography,
  IconButton as MuiIconButton,
  CircularProgress,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  Menu as MenuIcon,
  NotificationsNone as NotificationsIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import CircleIcon from '@mui/icons-material/Circle';
import { useNavigate, Link } from 'react-router-dom';
import { Person, Logout } from '@mui/icons-material';
import CakeIcon from '@mui/icons-material/Cake';
import AddIcon from '@mui/icons-material/Add';
import Color from './Color';
import { setCartItems } from '../store/cakeCartSlice';
import { useDispatch } from 'react-redux';
import { notificationService } from '../service/notificationService'

const fullDrawerWidth = 240;
const collapsedDrawerWidth = 72;

const Header = ({ currentDrawerWidth, handleMobileToggle, handleDesktopToggle, desktopOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // States
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [notifications, setNotifications] = useState({ unread: [], read: [] });
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingCount, setLoadingCount] = useState(true);

  // User role
  const userRole = JSON.parse(localStorage.getItem('role') || 'null');
  const showOrderButtons = userRole !== 3;

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await notificationService.getNotifications(1, 10);
      const allNotifications = response.data?.data || [];

      const unread = allNotifications.filter(n => !n.is_read);
      const read = allNotifications.filter(n => n.is_read);

      setNotifications({ unread, read });

      setUnreadCount(response.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      setLoadingCount(true);
      try {
        const response = await notificationService.getNotifications(1, 10);
        setUnreadCount(response.unread_count || 0);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      } finally {
        setLoadingCount(false);
      }
    };

    fetchUnreadCount();
  }, []);

  const handleAvatarClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Notification 
  const handleNotifOpen = () => {
    setNotifOpen(true);
    fetchNotifications();
  };
  const handleNotifClose = () => setNotifOpen(false);
  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    setLoading(true);
    try {
      await notificationService.readAll();
      await fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      await fetchNotifications();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleViewAll = () => {
    navigate('/notifications');
    handleNotifClose();
  };

  // NEW: Handle notification click with action_url navigation
  const handleNotificationClick = async (notif) => {
    // Mark as read if unread
    if (!notif.is_read) {
      await handleMarkAsRead(notif.id);
    }

    // Navigate to action_url if present
    if (notif.action_url) {
      try {
        const url = new URL(notif.action_url);
        navigate(url.pathname + url.search); // e.g., /view-order/57
        handleNotifClose(); // Close dialog after navigation
      } catch (error) {
        console.error('Invalid action_url:', notif.action_url);
        // Fallback
        window.location.href = notif.action_url;
      }
    }
  };

  // Format time 
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Just now';

    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60 / 60 / 24); // days

    if (diff === 0) return 'Today';
    if (diff === 1) return '1d ago';
    if (diff < 30) return `${diff}d ago`;
    return date.toLocaleDateString();
  };

  const renderNotificationItem = (notif, index) => (
    <ListItem
      key={notif.id || index}
      sx={{
        py: { xs: 2, sm: 1.5 },
        px: { xs: 3, sm: 2 },
        borderBottom: 1,
        borderColor: 'divider',
        alignItems: 'flex-start',
        '&:hover': { backgroundColor: 'action.hover' },
        cursor: 'pointer',
      }}
      onClick={() => handleNotificationClick(notif)} // Updated to handle action_url
    >
      <ListItemAvatar>
        <Avatar
          sx={{
            bgcolor: notif.avatarBg || Color.secondary.main,
            width: { xs: 40, sm: 32 },
            height: { xs: 40, sm: 32 },
            fontSize: { xs: '1rem', sm: '1rem' },
            fontWeight: 'bold',
          }}
        >
          {notif.branch?.name?.[0] || 'N'}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MuiTypography
              sx={{
                fontWeight: notif.is_read ? 400 : 600,
                fontSize: { xs: '0.9rem', sm: '0.875rem' },
                lineHeight: 1.4,
                color: notif.is_read ? 'text.secondary' : 'text.primary'
              }}
            >
              {notif.title || notif.message || notif.data?.message || 'New notification'}
            </MuiTypography>
            {!notif.is_read && (
              <CircleIcon
                sx={{
                  color: Color.primary.main,
                  fontSize: { xs: 10, sm: 10 },
                }}
              />
            )}
          </Box>
        }
        secondary={
          <MuiTypography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.8rem', sm: '0.8rem' }, mt: 0.5, display: 'flex', gap: 1 }}
          >
            <Typography sx={{ fontSize: { xs: 12, sm: 12, md: 14, lg: 14 } }}>{formatTime(notif.created_at || notif.time || notif.updated_at)}</Typography> - <Typography sx={{ fontSize: { xs: 12, sm: 12, md: 14, lg: 14 }, color: Color.primary.main }}>{notif.branch?.name || ''}</Typography>
          </MuiTypography>
        }
      />
    </ListItem>
  );

  const currentNotifications = tabValue === 0 ? notifications.unread : notifications.read;
  const unreadCountFinal = loadingCount ? 0 : unreadCount;
  const readCount = notifications.read.length;

  return (
    <>
      {/* ... AppBar and Toolbar unchanged ... */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(12px)',
          height: 70,
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          boxShadow: 'none',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          left: { md: `${desktopOpen ? fullDrawerWidth : collapsedDrawerWidth}px`, xs: 0 },
          width: {
            md: `calc(100% - ${desktopOpen ? fullDrawerWidth : collapsedDrawerWidth}px)`,
            xs: '100%',
          },
          transition: theme.transitions.create(['width', 'left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', minHeight: '64px', px: { xs: 2, sm: 3 } }}>
          {/* Left - Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={isTabletOrMobile ? handleMobileToggle : handleDesktopToggle}
              color="primary"
              size="large"
              sx={{
                borderRadius: '12px',
                color: Color.primary.main,
                p: 1,
                border: `1.5px solid ${Color.primary.main}`,
                marginTop: '6px',
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Right Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, marginTop: '6px' }}>

            {showOrderButtons && (
              <Button
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #D51721 0%, #ff5252 100%)',
                  boxShadow: '0 4px 12px rgba(213, 23, 33, 0.2)',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: { xs: 1.5, sm: 2.5 },
                  fontSize: { xs: '13px', sm: '14px' },
                  '&:hover': {
                    background: 'linear-gradient(135deg, #b9141c 0%, #ff1744 100%)',
                    boxShadow: '0 6px 15px rgba(213, 23, 33, 0.3)',
                  }
                }}
                onClick={() => {
                  localStorage.removeItem('selectedProducts');
                  localStorage.removeItem('cancelReferenceOrderNumber');
                  navigate('/create-order');
                }}
                startIcon={<AddIcon />}
              >
                Create Order
              </Button>
            )}

            {/* Notification Bell */}
            <Tooltip title="Notifications">
              <IconButton onClick={handleNotifOpen} sx={{ p: 1, color: Color.primary.main }}>
                <Badge badgeContent={unreadCountFinal > 99 ? '99+' : unreadCountFinal} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Settings - Admin only on desktop */}
            {userRole !== 3 && userRole !== 2 && !isMobile && (
              <Tooltip title="Settings">
                <IconButton component={Link} to="/settings" sx={{ p: 0, color: Color.primary.main }}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Profile Avatar */}
            <Box sx={{ display: 'flex', alignItems: 'center', pr: { xs: 0.7, md: 2 } }}>
              <Tooltip title="Profile">
                <Avatar
                  alt="User"
                  src="/profile.png"
                  sx={{ width: 36, height: 36, cursor: 'pointer', background: Color.primary.main }}
                  onClick={handleAvatarClick}
                >
                  <Person />
                </Avatar>
              </Tooltip>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{ elevation: 3, sx: { mt: 1.5, minWidth: 150 } }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }}>
                  <ListItemIcon><Person fontSize="small" /></ListItemIcon> Profile
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon><Logout fontSize="small" /></ListItemIcon> Logout
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Notification Dialog - unchanged structure, only click handler updated */}
      <Dialog
        open={notifOpen}
        onClose={handleNotifClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          style: {
            borderRadius: isMobile ? 0 : 8,
            overflow: 'hidden',
            margin: isMobile ? 0 : 'auto',
            height: isMobile ? '90vh' : '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: { xs: 2, sm: 2 },
            backgroundColor: '#f0f2f5',
            fontWeight: 500,
            minHeight: 64,
            flexShrink: 0,
          }}
        >
          <MuiTypography variant={isMobile ? 'h6' : 'h6'} fontWeight="bold">
            Notifications
            {loadingCount ? (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            ) : unreadCountFinal > 0 && (
              <Chip label={`${unreadCountFinal} unread`} size="small" color="error" sx={{ ml: 1 }} />
            )}
          </MuiTypography>
          <MuiIconButton onClick={handleNotifClose} size={isMobile ? 'medium' : 'medium'}>
            <CloseIcon fontSize={isMobile ? 'medium' : 'medium'} />
          </MuiIconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Tabs */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant={isMobile ? 'fullWidth' : 'standard'}
            centered
            TabIndicatorProps={{
              style: { backgroundColor: Color.secondary.main },
            }}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: '#f0f2f5',
              minHeight: 48,
              flexShrink: 0,
              '& .MuiTab-root': {
                fontSize: { xs: '.875rem', sm: '0.875rem' },
                fontWeight: 500,
                py: 1,
                color: 'black',
                opacity: 0.7,
              },
              '& .Mui-selected': {
                color: `${Color.secondary.main} !important`,
                fontWeight: 600,
                opacity: 1,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: Color.secondary.main,
              },
            }}
          >
            <Tab label={`New & Unread (${unreadCountFinal})`} />
            <Tab label={`Archive & Read (${readCount})`} />
          </Tabs>

          <Box sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {loading ? (
              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2
              }}>
                <List sx={{ p: 0 }}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <ListItem key={i} sx={{ py: 2 }}>
                      <ListItemAvatar>
                        <Skeleton variant="circular" width={32} height={32} />
                      </ListItemAvatar>
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="60%" height={20} sx={{ mb: 1 }} />
                        <Skeleton width="40%" height={12} />
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : (
              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {currentNotifications.length === 0 ? (
                  <Box sx={{
                    textAlign: 'center',
                    py: 8,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <MuiTypography variant="h6" color="text.secondary">
                      No {tabValue === 0 ? 'unread' : 'archived'} notifications
                    </MuiTypography>
                  </Box>
                ) : (
                  <List sx={{
                    flex: 1,
                    p: 0,
                    overflow: 'visible'
                  }}>
                    {currentNotifications.map((notif, index) => renderNotificationItem(notif, index))}
                  </List>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: 'space-between',
            p: { xs: 2, sm: 2 },
            borderTop: 1,
            borderColor: 'divider',
            gap: 1,
            minHeight: 56,
            flexShrink: 0,
          }}
        >
          <Button
            onClick={handleMarkAllAsRead}
            variant='outlined'
            disabled={unreadCountFinal === 0 || loading}
            sx={{
              fontSize: { xs: '0.7rem', sm: '0.7rem' },
              textTransform: 'none',
              border: '1px solid gray',
              color: 'black'
            }}
          >
            Mark all as read
          </Button>
          <Button
            variant="contained"
            onClick={handleViewAll}
            sx={{
              fontSize: { xs: '0.7rem', sm: '0.7rem' },
              px: { xs: 3, sm: 2 },
              textTransform: 'none',
              background: Color.secondary.main
            }}
          >
            View all notifications
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Header;